import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { TableRow, CircularProgress } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useInView } from 'react-intersection-observer';
import useSearchParams from '../../hooks/useSearchParams';
import useUrlFilters from '../../hooks/useUrlFilters';
import useUserPreferences, {
  TableDensity,
} from '../../hooks/useUserPreferences';
import CheckBox from '../Checkbox';
import { Flex } from '../Flex';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';
import { getValue } from '../../utils/common';
import ColumnFiltersMenu from './ColumnFiltersMenu';
import useColumnDragReorder from './hooks/useColumnDragReorder';
import type { BaseTableProps, Column, SortDirection } from './types';
import useTableFilteredData from './hooks/useTableFilteredData';
import {
  MIN_COLUMN_WIDTH,
  TABLE_ROW_ESTIMATE,
  TABLE_ROW_ESTIMATE_COMPACT,
  Wrapper,
  TableWrapper,
  EmptyStateWrapper,
  LoadingWrapper,
  StyledTable,
  TableHead,
  TableBody,
  ColumnActions,
  DraggableHeaderCell,
  TableCell,
  ExpandButtonCell,
  Resizer,
  HeaderCellContent,
  isRowSelectable,
  Row,
  SortButtons,
  TableLoadingState,
} from './TableShared';

// Re-export TableCell for external usage
export { TableCell } from './TableShared';

export type Props<T> = BaseTableProps<T> & {
  data?: Array<T>;
  scrollItemId?: string;
};

const Table = <T extends object>({
  data = [],
  columns,
  initialSelectedFilters,
  expandedRowRenderer,
  expandCondition,
  selectionColumn,
  defaultSortKey,
  defaultSortDirection = 'asc',
  emptyStateRenderer,
  selectedItems = [],
  onSelectItem,
  isSelectAll,
  onSelectAllChange,
  excludedItems = [],
  onExcludeItem,
  onFilteredItemsChange,
  scrollItemId,
  idKey,
  sx,
  wrapperSx,
  bottomScrollPadding,
  urlParamsPrefix,
  search,
  searchFunc,
  animateRows,
  focusedRows,
  centerRowCell = true,
  onRowClick,
  localStorageKey,
  isLoading = false,
  onScrollToBottom,
  isControlled,
  isFilterOptionsLoading,
  ...props
}: Props<T>) => {
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortKey);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);
  const [scrolledToItemDone, setScrolledToItemDone] = useState(false);
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const resizingColRef = useRef<string | null>(null);
  const [resizingCol, setResizingCol] = useState<string | null>(null);

  const handleResizeStart = (e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();

    resizingColRef.current = colId;
    setResizingCol(colId);

    const startX = e.pageX;
    const headerCell = (e.currentTarget as HTMLElement).closest('th');
    const startWidth = headerCell?.getBoundingClientRect().width || 0;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (resizingColRef.current !== colId) return;
      const currentX = moveEvent.pageX;
      const diff = currentX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + diff);

      setColWidths((prev) => ({
        ...prev,
        [colId]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      resizingColRef.current = null;
      setResizingCol(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeDoubleClick = (e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();

    setColWidths((prev) => {
      const newColWidths = { ...prev };
      delete newColWidths[colId];
      return newColWidths;
    });
  };

  const sortKey = useMemo(() => `${urlParamsPrefix}_sort`, [urlParamsPrefix]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { ref: scrollToBottomItemRef, inView: isScrollToEndItemInView } =
    useInView();

  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hideColumn),
    [columns],
  );

  const [toggledColumns] = useState(
    visibleColumns.filter((c) => !c.defaultUntoggled).map((c) => c.id),
  );

  const visibleColumnsToShow = useMemo(
    () =>
      columns.filter(
        (c) =>
          visibleColumns.find((vc) => vc.id === c.id) &&
          toggledColumns.find((tc) => tc === c.id),
      ),
    [columns, toggledColumns, visibleColumns],
  );

  const { filteredData, getFilterOptions } = useTableFilteredData<T>({
    data,
    columns,
    urlParamsPrefix,
    searchValue: search,
    searchFunc,
    isControlled,
  });

  const { urlFilters, addFilter, updateFilter } = useUrlFilters({
    urlKey: urlParamsPrefix,
    initialFilters: initialSelectedFilters,
  });

  const { userPreferences } = useUserPreferences();
  const [searchParams, setSearchParams] = useSearchParams();

  const density: TableDensity =
    (localStorageKey
      ? userPreferences.density?.[localStorageKey]
      : undefined) || 'regular';
  const isCompact = density === 'compact';

  useEffect(() => {
    if (urlParamsPrefix) {
      const sortParam = searchParams.get(sortKey);
      if (sortParam) {
        try {
          const { id, direction } = JSON.parse(sortParam);
          setSortBy(id);
          setSortDirection(direction as SortDirection);
          // eslint-disable-next-line no-empty
        } catch (e) {}
      }
    }
  }, [urlParamsPrefix, searchParams, sortKey]);

  const handleSort = useCallback(
    (id: string, direction: SortDirection) => {
      setSortBy(id);
      setSortDirection(direction);

      if (urlParamsPrefix) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(sortKey, JSON.stringify({ id, direction }));
        setSearchParams(newSearchParams);
      }
    },
    [urlParamsPrefix, searchParams, sortKey, setSearchParams],
  );

  const columnsToShow: Column<T>[] = useMemo(() => {
    const storageColumns = localStorageKey
      ? userPreferences.visibleColumns?.[localStorageKey]
      : undefined;

    const filteredColumns = storageColumns
      ? visibleColumns.filter(
          (c1) => c1.nonToggleable || storageColumns.find((c2) => c2 === c1.id),
        )
      : visibleColumnsToShow;

    // Separate regular columns from meta columns (meta columns always stay at the end)
    const regularColumns = filteredColumns.filter((c) => !c.metaColumn);
    const metaColumns = filteredColumns.filter((c) => c.metaColumn);

    // Apply saved column order if available (only to regular columns)
    const savedOrder = localStorageKey
      ? userPreferences.columnOrder?.[localStorageKey]
      : undefined;

    if (savedOrder && savedOrder.length > 0) {
      const orderedColumns: Column<T>[] = [];
      const remainingColumns = [...regularColumns];

      // First, add columns in the saved order (excluding meta columns)
      savedOrder.forEach((colId) => {
        const colIndex = remainingColumns.findIndex((c) => c.id === colId);
        if (colIndex !== -1) {
          orderedColumns.push(remainingColumns[colIndex]);
          remainingColumns.splice(colIndex, 1);
        }
      });

      // Then, add any remaining regular columns that weren't in the saved order
      orderedColumns.push(...remainingColumns);

      // Finally, append meta columns at the end (they always stay in their original order)
      return [...orderedColumns, ...metaColumns];
    }

    // No saved order, just ensure meta columns are at the end
    return [...regularColumns, ...metaColumns];
  }, [localStorageKey, userPreferences, visibleColumns, visibleColumnsToShow]);

  const {
    draggingColumnId,
    isReordering,
    columnTranslations,
    columnRefs,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useColumnDragReorder({
    columnsToShow,
    localStorageKey,
  });

  const sortedData = useMemo(() => {
    if (isControlled) {
      return filteredData;
    }

    if (sortBy && sortDirection) {
      const compareFunc = columnsToShow.find(
        (c) => c.id === sortBy,
      )?.compareFunc;

      const defaultCompare = (a: T, b: T) => {
        const aLower = getValue(a, sortBy)?.toString().toLowerCase();
        const bLower = getValue(b, sortBy)?.toString().toLowerCase();

        if (!aLower && !bLower) return 0;
        if (!aLower) return 1;
        if (!bLower) return -1;
        if (aLower < bLower) return -1;
        if (aLower > bLower) return 1;
        return 0;
      };

      const sortedAsc = compareFunc
        ? [...filteredData].sort((a, b) => compareFunc(a, b))
        : [...filteredData].sort(defaultCompare);

      return sortDirection === 'asc' ? sortedAsc : sortedAsc.reverse();
    }
    return filteredData;
  }, [columnsToShow, filteredData, sortBy, sortDirection, isControlled]);

  const showLoadingState = useMemo(
    () => isLoading && sortedData?.length === 0,
    [isLoading, sortedData?.length],
  );

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: filteredData.length,
    overscan: 20,
    estimateSize: () =>
      isCompact ? TABLE_ROW_ESTIMATE_COMPACT : TABLE_ROW_ESTIMATE,
    paddingEnd: bottomScrollPadding,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [isCompact, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;

  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  useEffect(() => {
    if (scrollItemId && idKey) {
      const scrollItemIndex = sortedData.findIndex(
        (item) => getValue(item, idKey) === scrollItemId,
      );

      const scrollToIndex = () =>
        rowVirtualizer.scrollToIndex(scrollItemIndex, {
          align: 'start',
        });

      setTimeout(() => {
        scrollToIndex();
        setTimeout(() => scrollToIndex(), 50);
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollItemId]);

  const isScrollItemRow = useCallback(
    (rowIndex: number) =>
      !!(
        idKey &&
        scrollItemId &&
        !scrolledToItemDone &&
        getValue(sortedData[rowIndex], idKey) === scrollItemId
      ),
    [idKey, scrolledToItemDone, scrollItemId, sortedData],
  );

  const handleRowSelect = (id: string) => {
    if (isSelectAll) {
      if (excludedItems?.includes(id)) {
        onExcludeItem?.(excludedItems.filter((item) => item !== id));
      } else {
        onExcludeItem?.([...excludedItems, id]);
      }
      return;
    }

    if (selectedItems?.includes(id)) {
      onSelectItem?.(selectedItems.filter((item) => item !== id));
    } else {
      onSelectItem?.([...selectedItems, id]);
    }
  };

  useEffect(() => {
    onSelectItem?.([]);
    onExcludeItem?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectAll]);

  useEffect(() => {
    onFilteredItemsChange?.(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  const tableMinWidth = useMemo(
    () =>
      columnsToShow.reduce((acc, c) => {
        const width = colWidths[c.id];
        if (width) return acc + width;
        if (typeof c.width === 'number') return acc + c.width;
        return acc + 165;
      }, 0),
    [columnsToShow, colWidths],
  );

  useEffect(() => {
    if (isScrollToEndItemInView) {
      onScrollToBottom?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollToEndItemInView]);

  return (
    <Wrapper sx={sx}>
      <TableWrapper
        ref={tableContainerRef}
        sx={{ minWidth: `${tableMinWidth}px`, ...wrapperSx }}
        $bottomScrollPadding={bottomScrollPadding}
      >
        <StyledTable {...props}>
          <TableHead $disabled={showLoadingState}>
            <TableRow role="row">
              {selectionColumn && (
                <TableCell
                  sx={{
                    width: '35px',
                    maxWidth: '35px',
                  }}
                  key="checkboxColumnHeader"
                  $customVariant="head"
                  $selectionColumn
                >
                  <CheckBox
                    checked={!!isSelectAll}
                    indeterminate={!!isSelectAll && excludedItems?.length > 0}
                    onChange={() => onSelectAllChange?.(!isSelectAll)}
                    $noPadding
                  />
                </TableCell>
              )}
              {columnsToShow.map((c, index) => (
                <DraggableHeaderCell
                  ref={(el: HTMLTableCellElement | null) => {
                    columnRefs.current[c.id] = el;
                  }}
                  sx={{
                    minWidth: colWidths[c.id] || c.width || 'auto',
                    width: colWidths[c.id] || c.width || 'auto',
                    boxSizing: 'border-box',
                  }}
                  aria-labelledby={c.header}
                  role="columnheader"
                  key={c.id}
                  $customVariant="head"
                  $isDragging={draggingColumnId === c.id}
                  $translateX={columnTranslations[c.id] || 0}
                  $isReordering={isReordering}
                  $isDraggable={!!localStorageKey && !c.metaColumn}
                  draggable={!!localStorageKey && !c.metaColumn}
                  onDragStart={(e) => handleDragStart(e, c.id)}
                  onDragOver={(e) => handleDragOver(e, c.id, index === 0)}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                >
                  <HeaderCellContent $centerContent={c.centerContent}>
                    <Flex style={{ gap: 12, overflow: 'hidden' }}>
                      <OverflowTooltip color="common.gray2">
                        {c.header}
                      </OverflowTooltip>

                      <ColumnActions
                        $isActive={
                          sortBy === c.id ||
                          urlFilters?.some((f) => f.property === c.id)
                        }
                      >
                        {(c.sortable || c.sortable === undefined) && (
                          <SortButtons
                            columnId={c.id}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                          />
                        )}
                        {(c.filterable || c.filterable === undefined) &&
                          !c.hideColumnFilter && (
                            <ColumnFiltersMenu
                              columnId={c.id}
                              isCounterHidden={c.isFilterCounterHidden}
                              getFilterOptions={getFilterOptions}
                              selectedFilters={urlFilters}
                              addFilter={addFilter}
                              updateFilter={updateFilter}
                              renderer={c.filterRenderer}
                              emptyValueLabel={c.emptyValueLabel}
                              showSelectAll={c.showSelectAll}
                              type={c.type}
                              sortOptions={c.sortFilterOptions !== false}
                              width={c.filterDropdownWidth}
                              isLoading={isFilterOptionsLoading}
                            />
                          )}
                      </ColumnActions>
                    </Flex>
                  </HeaderCellContent>
                  {!c.metaColumn && (
                    <Resizer
                      $isResizing={resizingCol === c.id}
                      onMouseDown={(e) => handleResizeStart(e, c.id)}
                      onDoubleClick={(e) => handleResizeDoubleClick(e, c.id)}
                    />
                  )}
                </DraggableHeaderCell>
              ))}
              {!!expandedRowRenderer && (
                <ExpandButtonCell
                  key="expandColumnHeader"
                  $customVariant="head"
                />
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {paddingTop > 0 && (
              <tr>
                <td aria-label="label" style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const isRowSelected =
                (isSelectAll &&
                  isRowSelectable(
                    selectionColumn,
                    sortedData[virtualRow.index],
                  ) &&
                  !excludedItems?.includes(
                    getValue(sortedData[virtualRow.index], idKey || 'id'),
                  )) ||
                (!isSelectAll &&
                  selectedItems?.includes(
                    getValue(sortedData[virtualRow.index], idKey || 'id'),
                  ));

              return (
                <Row
                  key={virtualRow.key}
                  rowData={sortedData[virtualRow.index]}
                  columns={columnsToShow}
                  columnTranslations={columnTranslations}
                  isReordering={isReordering}
                  onRowClick={onRowClick}
                  expandedRowRenderer={expandedRowRenderer}
                  expandCondition={expandCondition}
                  expandOnLoad={isScrollItemRow(virtualRow.index)}
                  selectionColumn={selectionColumn}
                  centerRowCell={centerRowCell}
                  compact={isCompact}
                  isSelected={isRowSelected}
                  onSelectChange={() =>
                    handleRowSelect(
                      getValue(sortedData[virtualRow.index], idKey || 'id'),
                    )
                  }
                  onLoaded={
                    isScrollItemRow(virtualRow.index)
                      ? () => setScrolledToItemDone(true)
                      : undefined
                  }
                  animate={animateRows?.includes(
                    getValue(sortedData[virtualRow.index], idKey || 'id'),
                  )}
                  isFocused={focusedRows?.includes(
                    getValue(sortedData[virtualRow.index], idKey || 'id'),
                  )}
                />
              );
            })}

            {paddingBottom > 0 && (
              <tr>
                <td
                  aria-label="label"
                  style={{ height: `${paddingBottom}px` }}
                />
              </tr>
            )}

            <tr>
              <td aria-label="scroll-to-bottom" colSpan={columnsToShow.length}>
                <div
                  ref={scrollToBottomItemRef}
                  style={{
                    visibility: 'collapse',
                    width: '100%',
                  }}
                />
              </td>
            </tr>
          </TableBody>
        </StyledTable>

        {!isLoading && sortedData && sortedData?.length === 0 && (
          <EmptyStateWrapper>{emptyStateRenderer?.()}</EmptyStateWrapper>
        )}

        {showLoadingState && <TableLoadingState minWidth={tableMinWidth} />}

        {isLoading && sortedData?.length > 0 && (
          <LoadingWrapper>
            <CircularProgress size={28} sx={{ my: 1 }} />
          </LoadingWrapper>
        )}
      </TableWrapper>

      {/* {!hidePagination && data?.length > ROWS_PER_PAGE && (
        <TablePagination
          sx={{ width: '100%', mt: -2 }}
          rowsPerPageOptions={[]}
          component="div"
          count={data.length}
          rowsPerPage={ROWS_PER_PAGE}
          page={page}
          onPageChange={handleChangePage}
        />
      )} */}
    </Wrapper>
  );
};

export default Table;
